import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { request } from '../../api/client'
import type { Setting, WriteSettingBody } from './types'

/** §8's table. One fetch, unenriched — `listSettings` (service.ts) already
    sorts by name and already reduces every setting to what is currently in
    effect (or "not set"), so there is nothing left to resolve client-side. */
export function useAdminSettings() {
  return useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => request<{ settings: Setting[] }>('/api/admin/settings'),
  })
}

/**
 * §8's PUT. `writeSetting` (service.ts) merges: an omitted field keeps its
 * stored value, and every rule about what may be written — the
 * `for_frontend`+`is_secret` conflict, the secret-downgrade refusal — is
 * enforced there, against the merged result, not duplicated here. This
 * mutation is deliberately dumb: it sends exactly the body it is given.
 */
export function useWriteAdminSetting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ name, body }: { name: string; body: WriteSettingBody }) =>
      request<{ setting: Setting | null }>(`/api/admin/settings/${encodeURIComponent(name)}`, {
        method: 'PUT',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] })
    },
  })
}
